import { AxiosError, AxiosResponse } from 'axios'
import { assign, interpret } from 'xstate'
import { createModel } from 'xstate/lib/model'

import { assign as immerAssign } from '@xstate/immer'

import {
  submitTransaction,
  submitUnannouncedTransaction,
} from 'src/components/IntakeFlow/ValidatedCount.utils'
import {
  queryMobileTransactions,
  updateTransactionLocation,
  updateTransactionStatus,
} from 'src/services/transactionService'
import {
  validateTransaction,
  validateUnannouncedTransaction,
} from 'src/services/transactionService'
import { Customer } from 'src/types/customer'
import { Depot } from 'src/types/depot'
import { WorkArea } from 'src/types/workArea'

import { FustAmount } from '../types/fust'
import {
  FustTransaction,
  TransactionStatus,
  TransactionType,
} from '../types/transaction'
import {
  getCustomerNumber,
  ScanFustPassFlowMachineContext,
} from './scanFustPassFlowMachine'

export type IntakeMobileTransactionsMachineContext = {
  listOfTransactions: FustTransaction[]
  selectedTransaction?: FustTransaction
  unannouncedCustomerNumber?: Customer['nummer']
  unannouncedCustomerName?: Customer['naam']
  fustAmount: FustAmount[]
  scannedFustPassContext?: ScanFustPassFlowMachineContext
  error?: string
  selectedReturnLocation?: Depot
  selectedWorkArea?: WorkArea
}

export const intakeMobileTransactionsMachineModel = createModel(
  {
    selectedWorkArea: undefined,
    listOfTransactions: [],
    selectedTransaction: undefined,
    selectedReturnLocation: undefined,
    fustAmount: [],
    error: undefined,
  } as IntakeMobileTransactionsMachineContext,
  {
    events: {
      ASSIGN_UNANNOUNCED_CUSTOMER_NUMBER: (
        customerNumber: Customer['nummer'],
        customerName: Customer['naam']
      ) => ({
        customerNumber,
        customerName,
      }),
      CLOSE_CUSTOMER_NUMBER_DIALOG: () => ({}),
      CLOSE_FUST_PASS_SCAN_DIALOGS: () => ({}),
      LOAD_TRANSACTIONS: () => ({}),
      CONTINUE_UNANNOUNCED: () => ({}),
      CONTINUE: () => ({}),
      ASSIGN_SCANNED_FUST_PASS_CONTEXT: (
        scannedFustPassContext?: ScanFustPassFlowMachineContext
      ) => ({ scannedFustPassContext }),
      ASSIGN_FUST_AMOUNT: (fustAmount: FustTransaction['fustAantallen']) => ({
        fustAmount,
      }),
      ASSIGN_SELECTED_TRANSACTION: (transaction: FustTransaction) => ({
        transaction,
      }),
      ASSIGN_SELECTED_RETURN_LOCATION: (depot: Depot) => ({
        depot,
      }),
      CLOSE_SUCCESS_DIALOG: () => ({}),
      CLOSE_TRANSACTION_DETAIL_VIEW: () => ({}),
      CLOSE_FUST_PASS_NUMBER_DIALOG: () => ({}),
      RESET_ERROR: () => ({}),
      EDIT_TRANSACTION: () => ({}),
    },
  }
)

export const intakeMobileTransactionsMachine =
  /** @xstate-layout N4IgpgJg5mDOIC5QEtawK4EMB2BjMAygC4D2ATmACpk6ya5HInawBiANiQO4B0AbsjBdk2KAH12qImJIAzMURot6jZrADEAVQByAQQIEAkgHFtY44YBqAUTMAlawGFrhgAqUx2zQFkAQtbtEUAAHElhkVWwgkAAPRABWeJ4ANgB2AAZk5IBOZPSAJnyADkSAGhAAT0QARnzq5J5a7OzU7KLUvKLkgGYAX17y1AwcfGJyKiU6BiYWDm5+QWFRCSkZeUVaFRmNAk1fb0MPS10AGUMAEV1Ka3OxB2c3Dy8-AOjQ8MjouIQAFmSkgr5ArxZIlbrddIg8pVX4-ao8IrdH4-Ir5HrparxAr9QZoLB4QikCjUTbTNRzXgCIQicSSWDSOQKSZbNTqSyGawAdTElDsum0BF0jkohgA8mZztZKLpDCc3mEIjMvog-gDCsDQfFwZDktCat1MTx4qifqlTd1shbsvkcSAhvjRkSJqTImxOJTFjSVvS1kyXdt1AAJfnnE7WMQEaxh4U3Hl8gVCkXigjyj5KpCxFX-HiAjVgiFQyqIZr5HjZaoZarVH75bqg7qpW32kaE8Yk5Rk2buhbU5Z0hnrZmdjQnUW6W68-mC4VigViM4ESipxXMZW-bO5-Ig-M6vUICupHPpTLxVK16rg2tNvEtsbEoeuik9pa01aMjYd13qfRGUwRxz8tosasJoi5iK4P5iI44rXAAGkuGbvCuUQZt8cJls0mFYZhqR7lW+TZI0ELpNamQYnU1TXsMBJ3s6n7bE+VIvt6A5+vRrI6IBoo6M45zLp8qFZmqQJbpq2qFjCRS5DmiJmj0DbIt0RRUQ6rb3v65LdkxXr9r6H5TF+P4mGYkbRtcE7xtOSYCvx6agN8eRGj8LSQl0UlFKa+R4ekNZEQUPn5MimLJCpt5Ou2BkMVpnp9m+g4aSw34GMZYggWBujeNx2gISECoCfZNQ+TmSnxBeHnJM5pXZHu2SJI0xQ-D5xrkT8oU0eFD5RfM2mxT676dRxriXNccZToms4RtKlCgbZq6Ceuwl5lqBa6kWCDJNU6QIki3RatatUQq1Ax2je7VtgNXbzOgwQQJgjDLPpLLYGI9J3egGgQMwYA8CIfAkAA1t9zZnep7GXbw123fd4iPZ2L1EG9sAIL9JC4HdMwANrpAAurNKEFQge08A2p7pEUJR-O0Px7iCpaIjW8QosRdTZG1jrnQlbpXTd6MPRd8OI+oYBkGQ5A8ME7B3bI5AALY-ad7Og5Fmnc1DXqw5EAtEO9yPYH9aORFjuOIXldmZvu1ZbXU7SgjWtWMzTtWNGkKJbnUxq7Wzal0cr4M8GAfCYOwWDQ2IXAABZ3WxvuwAoJASCQmAQOoeNrlWjU8NbHQeQRiTU2tjVbeCVpZDWQJ1l7tERU9XO8JwSfq-zERgDL6ifdgQN6wDQMK971fDk+9cQI3CViM3Mu6-r6PMEbqfzbUHllpCpWooieS1HuBHwsXbmNei5aVx1nOD4nw986P49CyLYsS1Lsvy9Ris+zXJ8N+fYNj0QLeT6j0-YLPJs0xzQJtUUiPBWggmaEpfIqQzzxD3MiJIWRUgmkKAeY08RD4czBrXZ8I8P4QDAAjZA7ANCOFHJGUaCYZzijEJKaUsoxDsi5HPEBltM7VBtjne2+cYQXkNMaQKZpUgWitDaY6wMn790fN2SO2AICSHfjHFOgDkJpzARAnIlpUSwK3DVbopYyZwixECVIoCKxYKVi-bsmBYD-S9Lgd6pAZbCzENgdAMsABGwskq-jMJxbQ2gsq8SgqBSgopvABE8D4fwgRVH5XNunK2nDs52zznuLohE4QiNSKeCszQpKWOfgPGxdiHFOJIC4sgbiPHeLIOochopKGODCREqJzxYl0MMKcUUxhWHm2cg0dIEJijZFdqAusGSUQyTrJ5Bslp4jiNxI-PuF1cFD3wco9unc-qAwfqpKuazX5nxhmsn+BtMY436d8Y0h4DwVTyGYkiiIapbWPOkDoYzMR1jMX0CRvdDnH27BspRNcr6izIOLSWRBpZkDlpI1ZQL5ggtOZzc5f8AG5SAfjRJ7Cs621zg7Na9Rt67TIkpG2BpGz-JWYCnBT5ICKlBcOdQDhmHcknNQ6yKZ4lm2+FShoiyijHikh8-4vDEDtCSPUM0jViJpAvEU6RXVeCMtDhrAMRk-ymScOZKhVlZw8qxWo+axQMgpA+RCUEZNdotAybUFILtQHkxEQUVmNKDlH3pd2NVmywVarMGlDwGUso5RAEhBJ3wt4-BzLVTE5MNprwlQgOBjrckGI+ReBs1LlmeuwTHJ8FAeqopwUGEMYZ9XjWTNcxAW4Lw8B+LtHIu1WhtCKN5AxxNwRjNNKCVEFcPVhXzdY+YRaYoluUTcQ4laaE2V5cA82dbugNqbQs1t5MMlZC7eCOEyIpU-EwYOkGxSZHzFwMwWQyA4VemEEQcOYhZBOLEMEWxOw9gHA8NBbQrBDB2G8FcCaQbwI-hrQgAVRpigiuyGKolfDjxILSKeXdloUE5pOrSr1BbuznuwJe69yxb33sfT6F9aAGkUPDEBiCBhokvDsF0npfT504qjYkJIYyxnxGg42kqq0YRtBjTvA9WRLSYiVUcrSwtkCyAqH6lloGDSIhzCk7UYyzQ0wPc7YVzVmjIjqOJpFlIg7IDVssdA2AcDYBIOZ-AEBo5PTbl9H6Xc9kIrpVh7qxnTPiHM5Z6zBI7MauYOiw2VzmPqKBOA2qWjoG6PgWtNeMzapAjJhWA9BnvWeckN5sQvnsBWZs5AeznZwU32hbC+FALMMjqM9l3mPmLP5f87Z4rkQQuXONsayNNQNHRagTouBGTF5GMhFWMViJKJHqkRJrLJn6utZmI5juzndk9ww8Okps2ctBewO1meYWut8p65FzR-WYGDeJSJGZiRih1BQdaDLHnatzfVRdUrkLb4wvvm56rm3nvbYunt-+B3w2mwXd8UBJ2+vaPO3o4lmIY02oPQ2QokGQpTcRZl3gGBPEywiK9zmKjDvg8QApIoxNYFSS3CieNNUKpaZ8o1Cm-x0e5qHVYv7PA5EKLk66F66BcdDEW9slb3d9ns5PSqrnOAefMr5zjvHaAZhA8xaD7Fa4D0YiNC0NI6ozEeQyWaNN2joMeVAY9mr0v5GKInTXfngulfMHe1Cu+cLxfHuVSrXg3ObcLbUPbxX4RgsowuftzrauTUE01-CLjHQzymM4cmza6FkG7T+B8xmxQLec5916PLBWAt+5YAHoXTuRco1c1Vjbp7vcy99-n5rRWduxwV6X3bIeMUg4jUd34WIY86-j5mg3CW0RpsRG0Mm5ZITZ5r1b2XDW-OFcC2skvjvsDO8+xV9303DNz-r41gvLXm+r6D+3ruofgfh+7yT3vWvY+64T8P-jhRHXCqUseVoMD0fHSs4Q+AGYftq8pdi0WI9I1lQNrRtcMguMSJ-Jvk8IIRycmguhPkgRSojo2cPcZsIYeYCcP5XptZ-9icWNJVIstQPJapP8uglIaZ6d6gcgKwPIzRcg0NACOdZ8A4g4Q4b1I5pBj9SAE4k5QNUQtpyDKoqCeh20C4IRt0ihagDQ7Z6CZ8pcUUi9P4W5QNgpl1GYDEUlXVKcEFqxX93ZTQKxCgtRlCvc8E5cZgxBCFiFSFhCQQEQURl5kg0Qsh4s+EDFCJBE2g2gegRELEMd3NLdc8bC1BNDjRCJAonU-hKwD1vJ60PZsktxoC0RLC-ZbF7FlhHF6RKlXF3EvFhZhDpl9o0QzFSpagOghtl1slRUc5wRFUQjftZ9VDm9hClIMJqxhUpIe1QEECi4kRxDqw0gKpD1MCd8sceBj9hYIVQNCgugKcd0ugGo-gEEM4ZUURoNJC-hWd0M812CpdfUIiWAFjrRBVywDpclNpFk+NiwipU8PkMgDFQRJtJjMcnseAx1exbdhwFjigGgGwVj3CPJ1jiUMRtD6YzDSY7jMjcEcM8M8cCMIgiMn1SMiCI9usEBo1lNjQKwZVMhqgkjCIshejrQckqx3iDiJdPc-Y+ApMZNedtgFMhVwFYELxMg+1ckMkWgG1mhEhQQOg0hPYWigCrDA46tQ4G8l8i8oiCIZJnJ-DNphVMQMkYFFSNoM1dF9i2DJcJSvN5sdtND3CYjaYUESY4RrQ8IEcjQPJIRWh9d4SnxW8iA8CY5WTih2TfkuSXUvDCpQFX9J8YEGwqTnTZE69mT-dW819QM4Q0hwFm00RGY39-SEBdNnYUFY9TxEgB0PjQic9IyzMD9G9l9OYT8zZr8SDfhnkEQxk6xkzIFiSEtcktMdDMRWglIMCaSsDd8fcwAi9Y45jyA4ywSywqcMQqZSpvINTi5EgUE38ShlIxSjirDz0ZYJYiEm9wDwt5oURkRxzSIUkD1mzJIuNX85DoM9omDwzuBNDDxsJHzmhcJiVyd3lFzrRMQCgRF+h+ggA */
  intakeMobileTransactionsMachineModel.createMachine(
    {
      context: intakeMobileTransactionsMachineModel.initialContext,
      tsTypes: {} as import('./intakeMobileTransactionsMachine.typegen').Typegen0,
      schema: {
        services: {} as {
          loadTransactions: { data: AxiosResponse<FustTransaction[]> }
          handleTransaction: {
            data: void
          }
          handleUnannouncedTransaction: {
            data: FustTransaction
          }
          validateUnannouncedTransaction: {
            data: AxiosResponse<FustAmount[]>
          }
          validateTransaction: {
            data: AxiosResponse<FustAmount[]>
          }
          updateStatus: {
            data: AxiosResponse<void>
          }
          resetStatus: {
            data: AxiosResponse<void>
          }
          updateLocation: {
            data: void
          }
        },
      },
      predictableActionArguments: true,
      id: 'intakeMobileTransactionsFlow',
      initial: 'viewing_list_of_transactions',
      states: {
        viewing_list_of_transactions: {
          on: {
            ASSIGN_SELECTED_TRANSACTION: {
              actions: 'assignSelectedTransaction',
              target: 'updating_status',
            },
            LOAD_TRANSACTIONS: {
              target: 'loading_transactions',
            },
            CONTINUE_UNANNOUNCED: {
              target: 'handling_unannounced_fust_pass_scan',
            },
          },
          exit: ['resetTransactionStatus'],
        },
        viewing_transaction_details: {
          on: {
            CLOSE_TRANSACTION_DETAIL_VIEW: {
              target: 'resetting_status',
            },
            CONTINUE: {
              target: 'handling_fust_pass_scan',
            },
          },
        },
        handling_unannounced_fust_pass_scan: {
          on: {
            ASSIGN_SCANNED_FUST_PASS_CONTEXT: [
              {
                actions: 'assignScannedFustPassContext',
                target: 'evaluate_customer_type',
              },
            ],
            CLOSE_FUST_PASS_NUMBER_DIALOG: [
              {
                cond: 'hasTransactionSelected',
                target: 'viewing_transaction_details',
              },
              {
                target: 'viewing_list_of_transactions',
              },
            ],
          },
        },
        handling_fust_pass_scan: {
          description: 'using <ScanFustPassFlowDialog /> to validate the number',
          on: {
            ASSIGN_SCANNED_FUST_PASS_CONTEXT: [
              {
                actions: 'assignScannedFustPassContext',
                target: 'count_transaction',
              },
            ],
            CLOSE_FUST_PASS_NUMBER_DIALOG: [
              {
                cond: 'hasTransactionSelected',
                target: 'viewing_transaction_details',
              },
              {
                target: 'viewing_list_of_transactions',
              },
            ],
          },
        },
        loading_transactions: {
          invoke: {
            src: 'loadTransactions',
            onDone: [
              {
                actions: 'assignLoadedTransactions',
                target: 'viewing_list_of_transactions',
              },
            ],
            onError: [
              {
                target: 'viewing_list_of_transactions',
                actions: 'assignError',
              },
            ],
          },
          tags: 'loading',
        },
        updating_status: {
          tags: ['loading'],
          invoke: {
            src: 'updateStatus',
            onDone: [
              {
                target: 'viewing_transaction_details',
              },
            ],
            onError: [
              {
                target: 'viewing_list_of_transactions',
              },
            ],
          },
        },
        resetting_status: {
          tags: ['loading'],
          invoke: {
            src: 'resetStatus',
            onDone: [
              {
                actions: 'clearSelectedTransaction',
                target: 'viewing_list_of_transactions',
              },
            ],
            onError: [
              {
                target: 'viewing_transaction_details',
              },
            ],
          },
        },
        evaluate_customer_type: {
          always: [
            {
              cond: 'isTransporter',
              target: 'asking_customer_number',
            },
            { target: 'count_transaction' },
          ],
        },
        asking_customer_number: {
          on: {
            ASSIGN_UNANNOUNCED_CUSTOMER_NUMBER: {
              actions: 'assignUnannouncedCustomerNumber',
              target: 'count_transaction',
            },
            CLOSE_CUSTOMER_NUMBER_DIALOG: {
              target: 'viewing_list_of_transactions',
            },
          },
        },
        count_transaction: {
          on: {
            ASSIGN_FUST_AMOUNT: {
              actions: 'assignFustAmount',
            },
            CONTINUE: {
              cond: 'hasFustAmounts',
              target: 'verifying_transaction',
            },
          },
          exit: ['resetTransactionStatus'],
        },
        reviewing_transaction: {
          on: {
            CONTINUE: {
              target: 'confirming_with_fust_pass',
            },
            EDIT_TRANSACTION: {
              target: 'count_transaction',
            },
          },
          exit: ['resetTransactionStatus'],
        },
        confirming_with_fust_pass: {
          on: {
            CONTINUE: {
              target: 'submitting_transaction',
            },
            CLOSE_FUST_PASS_NUMBER_DIALOG: {
              target: 'reviewing_transaction',
            },
          },
        },
        verifying_transaction: {
          always: [
            {
              cond: 'hasTransactionSelected',
              target: 'validating_transaction',
            },
            {
              target: 'validating_unannounced_transaction',
            },
          ],
        },
        validating_unannounced_transaction: {
          invoke: {
            src: 'validateUnannouncedTransaction',
            onDone: [
              {
                actions: 'assignValidatedFustAmount',
                target: 'reviewing_transaction',
              },
            ],
            onError: [
              {
                target: 'count_transaction',
                actions: 'assignError',
              },
            ],
          },
          tags: 'loading',
        },
        validating_transaction: {
          invoke: {
            src: 'validateTransaction',
            onDone: [
              {
                actions: 'assignValidatedFustAmount',
                target: 'reviewing_transaction',
              },
            ],
            onError: [
              {
                target: 'count_transaction',
                actions: 'assignError',
              },
            ],
          },
          tags: 'loading',
        },
        return_location: {
          on: {
            ASSIGN_SELECTED_RETURN_LOCATION: {
              actions: 'assignSelectedReturnLocation',
            },
            CONTINUE: {
              cond: 'hasReturnLocation',
              target: 'handeling_location_update',
            },
          },
        },
        handeling_location_update: {
          invoke: {
            src: 'updateLocation',
            onDone: 'showing_success_dialog',
            onError: [
              {
                target: 'return_location',
                actions: 'assignError',
              },
            ],
          },
          tags: 'loading',
        },
        submitting_transaction: {
          always: [
            {
              cond: 'hasTransactionSelected',
              target: 'handling_transactions_submission',
            },
            {
              target: 'handling_unannounced_transactions_submission',
            },
          ],
        },
        handling_transactions_submission: {
          invoke: {
            src: 'handleTransaction',
            onDone: [
              {
                target: 'return_location',
              },
            ],
            onError: [
              {
                target: 'reviewing_transaction',
                actions: 'assignError',
              },
            ],
          },
          tags: 'loading',
        },
        handling_unannounced_transactions_submission: {
          invoke: {
            src: 'handleUnannouncedTransaction',
            onDone: [
              {
                // store the create unannounced tx in selectedtransaction
                actions: assign((_, event) => ({
                  selectedTransaction: event.data,
                })),
                target: 'return_location',
              },
            ],
            onError: [
              {
                target: 'reviewing_transaction',
                actions: 'assignError',
              },
            ],
          },
          tags: 'loading',
        },
        showing_success_dialog: {
          on: {
            CLOSE_SUCCESS_DIALOG: {
              actions: 'assignInitialContext',
              target: '#intakeMobileTransactionsFlow',
            },
          },
        },
      },
      on: {
        RESET_ERROR: {
          actions: 'resetError',
        },
      },
    },
    {
      guards: {
        hasTransactionSelected: context => !!context.selectedTransaction,
        hasFustAmounts: context => context.fustAmount.length > 0,
        hasReturnLocation: context => !!context.selectedReturnLocation,
        isTransporter: context =>
          context.scannedFustPassContext?.scannedFustPass?.klant.type === 1,
      },
      actions: {
        resetTransactionStatus: context => {
          context.selectedTransaction &&
            updateTransactionStatus(context.selectedTransaction.id, {
              NewStatus: TransactionStatus.Nieuw,
            })
        },
        assignInitialContext: assign(context => {
          return {
            ...intakeMobileTransactionsMachineModel.initialContext,
            selectedWorkArea: context.selectedWorkArea,
          }
        }),
        clearSelectedTransaction: assign(_context => {
          return {
            selectedTransaction: undefined,
          }
        }),
        assignError: assign((_context, event) => ({
          error: (event.data as AxiosError<string>).response?.data,
        })),
        resetError: assign(_ => ({ error: undefined })),
        assignLoadedTransactions: assign((_context, event) => {
          return {
            listOfTransactions: event.data.data,
          }
        }),
        assignFustAmount: assign((_context, event) => {
          return {
            fustAmount: event.fustAmount,
          }
        }),
        assignValidatedFustAmount: assign((_context, event) => {
          return {
            fustAmount: event.data.data,
          }
        }),
        assignSelectedTransaction: assign((_context, event) => {
          const selectedTransaction = event.transaction
          if (selectedTransaction.status === 30) {
            return {
              selectedTransaction,
              fustAmount: event.transaction.fustAantallen,
            }
          } else {
            return {
              selectedTransaction,
            }
          }
        }),
        assignScannedFustPassContext: assign((_context, event) => {
          return {
            scannedFustPassContext: event.scannedFustPassContext,
          }
        }),
        assignSelectedReturnLocation: assign((_context, event) => {
          return {
            selectedReturnLocation: event.depot,
          }
        }),
        assignUnannouncedCustomerNumber: immerAssign((context, event) => {
          context.scannedFustPassContext!.additionalCustomerNumber =
            event.customerNumber
          context.scannedFustPassContext!.additionalCustomerName = event.customerName
        }),
      },
      services: {
        updateLocation: async context => {
          const updatedTransaction: FustTransaction = {
            ...context.selectedTransaction!,
            locatie: context.selectedReturnLocation!.code,
          }

          await updateTransactionLocation(updatedTransaction)

          await updateTransactionStatus(updatedTransaction.id!, {
            FustpasNummer:
              context.scannedFustPassContext?.scannedFustPass?.fustpasNummer,
            NewStatus: TransactionStatus.Bevestigd,
          })
        },
        updateStatus: context =>
          updateTransactionStatus(context.selectedTransaction!.id, {
            FustpasNummer: null,
            NewStatus: TransactionStatus['In behandeling'],
          }),
        resetStatus: context =>
          updateTransactionStatus(context.selectedTransaction!.id, {
            FustpasNummer: null,
            NewStatus: TransactionStatus.Nieuw,
          }),
        handleTransaction: context =>
          submitTransaction(
            context.selectedTransaction!,
            context.scannedFustPassContext!.scannedFustPass!.fustpasNummer,
            context.fustAmount
          ),
        handleUnannouncedTransaction: async context =>
          submitUnannouncedTransaction(
            String(getCustomerNumber(context.scannedFustPassContext!)),
            TransactionType.InnameMobiel,
            context.fustAmount,
            context.scannedFustPassContext!.scannedFustPass!.fustpasNummer,
            context.selectedWorkArea!.vestiging,
            context.scannedFustPassContext!.scannedFustPass!.klant.nummer
          ),
        loadTransactions: async context =>
          queryMobileTransactions({
            Klantnummer: null,
            VervoerdersNummer: null,
            TransactieType: TransactionType.InnameMobiel,
            Vestiging: localStorage.getItem('mfb-selected-branch')!, // eslint-disable-line
            Status: [TransactionStatus.Nieuw, TransactionStatus['In behandeling']],
            WerkGebied: context.selectedWorkArea?.naam,
          }),
        validateUnannouncedTransaction: context =>
          validateUnannouncedTransaction(
            getCustomerNumber(context.scannedFustPassContext!),
            {
              klantNaam: null,
              klantnummer: null,
              locatie: null,
              status: null,
              transactieID: null,
              transactieType: TransactionType.InnameMobiel,
              vervoerderNummer: null,
              datum: null,
              fustAantallen: context.fustAmount!,
            }
          ),
        validateTransaction: context =>
          validateTransaction(
            context.selectedTransaction!.id,
            getCustomerNumber(context.scannedFustPassContext!),
            {
              ...context.selectedTransaction!,
              fustAantallen: context.fustAmount!,
            }
          ),
      },
    }
  )

export const intakeMobileTransactionsService = interpret(
  intakeMobileTransactionsMachine
)
